import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import styles from './SettingsPage.module.css';
import loadingStyles from '../shared/Loading.module.css';
import { SuccessToast, useSuccessToast } from '../shared/SuccessToast';
import config, { logger } from '../../config';
import { persistAuthUser } from '../../utils/helper';
import {
  getToken,
  fetchUserSettings,
  updateUserProfile,
  changeUserPassword,
  deactivateUserAccount,
} from '../../utils/api';

const LOADING_MESSAGE = 'Loading settings...';
const DEFAULT_FETCH_ERROR = 'Failed to load settings';
const NETWORK_ERROR = 'Network error. Please try again.';
const FOOTER_YEAR = new Date().getFullYear();
const MODAL_EASE = [0.16, 1, 0.3, 1];
const PASSWORD_FIELDS = [
  { key: 'oldPassword', id: 'settings-old-password', label: 'Current password', autoComplete: 'current-password' },
  { key: 'newPassword', id: 'settings-new-password', label: 'New password', autoComplete: 'new-password' },
  { key: 'confirmPassword', id: 'settings-confirm-password', label: 'Confirm new password', autoComplete: 'new-password' },
];

const overlayMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.28, ease: MODAL_EASE },
};
const modalMotion = {
  initial: { opacity: 0, y: 22, scale: 0.972 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 12, scale: 0.985 },
  transition: { duration: 0.34, ease: MODAL_EASE },
};
const pageContainerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};
const pageSectionVariants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.44, ease: MODAL_EASE } },
};
const nestedStaggerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
};
const nestedItemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.32, ease: MODAL_EASE } },
};
const pageFadeMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.3, ease: MODAL_EASE },
};
const editButtonMotion = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.96 },
  transition: { duration: 0.2, ease: MODAL_EASE },
};
const formActionsMotion = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 6 },
  transition: { duration: 0.24, ease: MODAL_EASE },
};
const modalErrorMotion = {
  initial: { opacity: 0, y: -6, height: 0 },
  animate: { opacity: 1, y: 0, height: 'auto' },
  exit: { opacity: 0, y: -4, height: 0 },
  transition: { duration: 0.22, ease: MODAL_EASE },
};

const cx = (...parts) => parts.filter(Boolean).join(' ');

function withMotion(preset, reduce) {
  if (!reduce) return preset;
  return { initial: false, animate: preset.animate, exit: preset.exit, transition: { duration: 0 } };
}

function withVariantMotion(reduce) {
  return reduce ? { initial: false, animate: 'show' } : { initial: 'hidden', animate: 'show' };
}

function profileFromSettings(data) {
  return {
    fullName: data?.personal_info?.full_name ?? '',
    phone: data?.personal_info?.phone ?? '',
  };
}

function initialsFromName(name) {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'R';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function displayValue(value, fallback = 'Not set') {
  const text = String(value ?? '').trim();
  return text || fallback;
}

function prependAuditLogEntry(existingLogs, entry) {
  if (!entry?.id) return existingLogs ?? [];
  const current = Array.isArray(existingLogs) ? existingLogs : [];
  if (current.some((log) => log.id === entry.id)) return current;
  return [entry, ...current];
}

function formatAuditLogDate(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [active]);
}

function SvgIcon({ size = 22, fill = 'none', children }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} aria-hidden="true">
      {children}
    </svg>
  );
}

function VerifiedIcon() {
  return (
    <SvgIcon size={18}>
      <path d="M12 2l7 3v6c0 5-3.5 9.5-7 11-3.5-1.5-7-6-7-11V5l7-3z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

function PasswordIcon() {
  return (
    <SvgIcon>
      <path d="M8 10V8a4 4 0 118 0v2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="12" cy="15" r="1" fill="currentColor" />
      <path d="M12 16v1.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </SvgIcon>
  );
}

function AuditLogIcon() {
  return (
    <SvgIcon>
      <path d="M8 4h8a2 2 0 012 2v14l-4-2-4 2-4-2V6a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M9 10h6M9 14h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </SvgIcon>
  );
}

function WarningIcon() {
  return (
    <SvgIcon size={22}>
      <path d="M12 3.5 21.5 20h-19L12 3.5z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M12 9.5v4.25" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="12" cy="16.75" r="1" fill="currentColor" />
    </SvgIcon>
  );
}

function LockIcon() {
  return (
    <SvgIcon size={14}>
      <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path d="M8 10V8a4 4 0 118 0v2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </SvgIcon>
  );
}

function PencilIcon() {
  return (
    <SvgIcon size={16}>
      <path d="M4 16.4V20h3.6L19 8.6 15.4 5 4 16.4z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M13.2 7.2l3.6 3.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </SvgIcon>
  );
}

function ChevronIcon() {
  return (
    <SvgIcon size={18}>
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

function ErrorIcon() {
  return (
    <SvgIcon fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
    </SvgIcon>
  );
}

function SectionHeader() {
  return (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionHeading}>
        <h2 className={styles.sectionTitle}>Account Settings</h2>
        <p className={styles.sectionSubtitle}>
          Manage your profile, security, and account preferences.
        </p>
      </div>
    </div>
  );
}

function ButtonSpinner() {
  return (
    <span className={styles.btnSpinner} aria-hidden="true">
      <span className={styles.btnSpinnerInner} />
    </span>
  );
}

function ActionButton({ busy, busyLabel, children, className, type = 'button', disabled, ...props }) {
  return (
    <button type={type} className={className} disabled={disabled || busy} aria-busy={Boolean(busy)} {...props}>
      {busy ? (
        <>
          <ButtonSpinner />
          {busyLabel}
        </>
      ) : (
        children
      )}
    </button>
  );
}

function Field({ id, label, className, inputClassName, hint, locked = false, ...inputProps }) {
  return (
    <div className={className}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
        {locked && (
          <span className={styles.fieldLock} aria-hidden="true">
            <LockIcon />
          </span>
        )}
      </label>
      <input id={id} className={cx(styles.fieldInput, locked && styles.fieldInputLocked, inputClassName)} {...inputProps} />
      {hint && <p className={styles.fieldHint}>{hint}</p>}
    </div>
  );
}

function ReadField({ label, value, locked = false, hint }) {
  return (
    <div className={styles.readField}>
      <span className={styles.readFieldLabel}>
        {label}
        {locked && (
          <span className={styles.fieldLock} aria-hidden="true">
            <LockIcon />
          </span>
        )}
      </span>
      <span className={cx(styles.readFieldValue, !value && styles.readFieldEmpty)}>
        {displayValue(value)}
      </span>
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </div>
  );
}

function ModalHeader({ id, title, icon, onClose, closeDisabled = false }) {
  return (
    <div className={styles.modalHeader}>
      <div className={styles.modalHeaderText}>
        {icon && (
          <span className={styles.modalHeaderIcon} aria-hidden="true">{icon}</span>
        )}
        <h2 id={id} className={styles.modalTitle}>{title}</h2>
      </div>
      <button
        type="button"
        className={styles.modalCloseButton}
        onClick={onClose}
        disabled={closeDisabled}
        aria-label="Close"
      >
        ×
      </button>
    </div>
  );
}

function ModalError({ error, shouldReduceMotion }) {
  return (
    <AnimatePresence>
      {error && (
        <motion.p className={styles.modalError} role="alert" {...withMotion(modalErrorMotion, shouldReduceMotion)}>
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

function SettingsModal({
  isOpen,
  onClose,
  title,
  titleId,
  icon,
  closeDisabled,
  panelClassName,
  subtitle,
  shouldReduceMotion,
  children,
}) {
  useBodyScrollLock(isOpen);
  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.modalOverlay}
          role="presentation"
          {...withMotion(overlayMotion, shouldReduceMotion)}
        >
          <motion.div
            className={cx(styles.modal, panelClassName)}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            {...withMotion(modalMotion, shouldReduceMotion)}
          >
            <ModalHeader
              id={titleId}
              title={title}
              icon={icon}
              onClose={onClose}
              closeDisabled={closeDisabled}
            />
            {subtitle && <p className={styles.modalSubtitle}>{subtitle}</p>}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

function SecurityItem({ icon, title, description, actionLabel, onAction }) {
  return (
    <motion.div variants={nestedItemVariants}>
      <button type="button" className={styles.securityItem} onClick={onAction}>
        <span className={styles.securityIcon} aria-hidden="true">{icon}</span>
        <span className={styles.securityContent}>
          <span className={styles.securityItemTitle}>{title}</span>
          <span className={styles.securityItemDescription}>{description}</span>
        </span>
        <span className={styles.securityAction}>
          <span className={styles.securityActionLabel}>{actionLabel}</span>
          <ChevronIcon />
        </span>
      </button>
    </motion.div>
  );
}

function ChangePasswordModal({ isOpen, onClose, onSuccess, shouldReduceMotion }) {
  const [fields, setFields] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    setFields({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setError('');
    setSaving(false);
  }, [isOpen]);

  const handleChange = (key) => (event) => {
    setFields((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const { oldPassword, newPassword, confirmPassword } = fields;
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError('All password fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (oldPassword === newPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    setSaving(true);
    const result = await changeUserPassword({ oldPassword, newPassword, confirmPassword }, navigate);
    setSaving(false);

    if (!result?.ok) {
      setError(result?.message ?? 'Failed to change password.');
      return;
    }

    onSuccess(result?.data?.audit_log_entry);
    onClose();
  };

  return (
    <SettingsModal
      isOpen={isOpen}
      onClose={onClose}
      title="Change Password"
      titleId="change-password-title"
      subtitle="Choose a strong password you have not used before."
      closeDisabled={saving}
      shouldReduceMotion={shouldReduceMotion}
    >
      <ModalError error={error} shouldReduceMotion={shouldReduceMotion} />
      <motion.form
        className={styles.modalForm}
        onSubmit={handleSubmit}
        variants={nestedStaggerVariants}
        {...withVariantMotion(shouldReduceMotion)}
      >
        {PASSWORD_FIELDS.map((field) => (
          <motion.div key={field.key} variants={nestedItemVariants}>
            <Field
              id={field.id}
              label={field.label}
              type="password"
              className={styles.fieldGroup}
              value={fields[field.key]}
              onChange={handleChange(field.key)}
              autoComplete={field.autoComplete}
              disabled={saving}
            />
          </motion.div>
        ))}
        <motion.div className={styles.formActions} variants={nestedItemVariants}>
          <button type="button" className={styles.cancelButton} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <ActionButton type="submit" className={styles.saveButton} busy={saving} busyLabel="Updating...">
            Update Password
          </ActionButton>
        </motion.div>
      </motion.form>
    </SettingsModal>
  );
}

function AuditLogModal({ isOpen, onClose, logs, shouldReduceMotion }) {
  const auditLogs = Array.isArray(logs) ? logs : [];

  return (
    <SettingsModal
      isOpen={isOpen}
      onClose={onClose}
      title="Privacy Audit Log"
      titleId="audit-log-title"
      subtitle="A history of changes made to your personal data."
      panelClassName={styles.modalWide}
      shouldReduceMotion={shouldReduceMotion}
    >
      {auditLogs.length === 0 ? (
        <div className={styles.auditLogEmpty}>
          <div className={styles.auditLogEmptyIcon} aria-hidden="true">
            <AuditLogIcon />
          </div>
          <p className={styles.auditLogEmptyTitle}>No activity yet</p>
          <p>Profile changes will appear here once you update your details.</p>
        </div>
      ) : (
        <motion.ul
          className={styles.auditLogList}
          variants={nestedStaggerVariants}
          {...withVariantMotion(shouldReduceMotion)}
        >
          {auditLogs.map((log) => (
            <motion.li
              key={log.id}
              className={styles.auditLogItem}
              variants={nestedItemVariants}
            >
              <p className={styles.auditLogDescription}>{log.description}</p>
              <p className={styles.auditLogDate}>{formatAuditLogDate(log.created_at)}</p>
            </motion.li>
          ))}
        </motion.ul>
      )}
    </SettingsModal>
  );
}

function DeactivateAccountModal({ isOpen, onClose, onConfirm, deactivating, error, shouldReduceMotion }) {
  return (
    <SettingsModal
      isOpen={isOpen}
      onClose={onClose}
      title="Deactivate Account"
      titleId="deactivate-account-title"
      icon={<WarningIcon />}
      closeDisabled={deactivating}
      panelClassName={styles.modalDanger}
      shouldReduceMotion={shouldReduceMotion}
    >
      <ModalError error={error} shouldReduceMotion={shouldReduceMotion} />
      <p className={styles.confirmModalMessage}>
        Deactivating your account will sign you out and begin removing your Richuel data and coaching
        history. Your information will be fully removed after 30 days, in line with our
        privacy policy. This action cannot be undone.
      </p>
      <p className={styles.confirmModalMessage}>
        Are you sure you want to deactivate your account?
      </p>
      <div className={styles.formActions}>
        <button type="button" className={styles.cancelButton} onClick={onClose} disabled={deactivating}>
          Cancel
        </button>
        <ActionButton
          className={cx(styles.saveButton, styles.dangerConfirmButton)}
          onClick={onConfirm}
          busy={deactivating}
          busyLabel="Deactivating..."
        >
          Deactivate Account
        </ActionButton>
      </div>
    </SettingsModal>
  );
}

function LoadingState({ shouldReduceMotion }) {
  return (
    <motion.div
      className={styles.loadingShell}
      aria-busy="true"
      aria-live="polite"
      {...withMotion(pageFadeMotion, shouldReduceMotion)}
    >
      <div className={styles.loadingCard}>
        <div className={loadingStyles['loading-container']}>
          <div className={loadingStyles['loading-spinner']}>
            <div className={loadingStyles['spinner-ring']} />
            <div className={loadingStyles['spinner-ring']} />
            <div className={loadingStyles['spinner-ring']} />
          </div>
          <p>{LOADING_MESSAGE}</p>
        </div>
      </div>
    </motion.div>
  );
}

function ErrorState({ message, shouldReduceMotion }) {
  return (
    <motion.div
      className={styles.errorContainer}
      role="alert"
      {...withMotion(pageFadeMotion, shouldReduceMotion)}
    >
      <div className={styles.errorCard}>
        <span className={styles.errorIcon} aria-hidden="true">
          <ErrorIcon />
        </span>
        <p className={styles.errorMessage}>{message}</p>
      </div>
    </motion.div>
  );
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const {
    message: saveMessage,
    isExiting: saveMessageExiting,
    showToast: showSaveMessage,
    dismissToast: dismissSaveMessage,
  } = useSuccessToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [openModal, setOpenModal] = useState(null);
  const [deactivatingAccount, setDeactivatingAccount] = useState(false);
  const [deactivateAccountError, setDeactivateAccountError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    const controller = new AbortController();

    const loadSettings = async () => {
      const token = getToken();
      if (!token) {
        localStorage.clear();
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        setError('');
        const data = await fetchUserSettings(navigate, controller.signal);
        if (controller.signal.aborted) return;
        if (!data) {
          setError(DEFAULT_FETCH_ERROR);
          return;
        }
        setSettings(data);
        const profile = profileFromSettings(data);
        setFullName(profile.fullName);
        setPhone(profile.phone);
        if (data?.personal_info?.email) {
          persistAuthUser({ email: data.personal_info.email });
        }
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        logger.error('Error fetching settings:', err);
        setError(NETWORK_ERROR);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadSettings();
    return () => controller.abort();
  }, [navigate]);

  const handleEditClick = useCallback(() => {
    dismissSaveMessage();
    setIsEditing(true);
  }, [dismissSaveMessage]);

  const handleCancelEdit = useCallback(() => {
    const profile = profileFromSettings(settings);
    setFullName(profile.fullName);
    setPhone(profile.phone);
    dismissSaveMessage();
    setIsEditing(false);
  }, [settings, dismissSaveMessage]);

  const handleSave = useCallback(async () => {
    const trimmedName = fullName.trim();
    const trimmedPhone = phone.trim();
    if (trimmedName.length < 2) {
      showSaveMessage('Full name must be at least 2 characters.');
      return;
    }

    setSaving(true);
    dismissSaveMessage();

    const result = await updateUserProfile({ name: trimmedName, phone: trimmedPhone }, navigate);
    setSaving(false);

    if (!result?.ok) {
      showSaveMessage(result?.message ?? 'Failed to update profile.');
      return;
    }

    localStorage.setItem(config.STORAGE_KEYS.NAME, trimmedName);
    setSettings((prev) => ({
      ...prev,
      personal_info: {
        ...prev?.personal_info,
        full_name: trimmedName,
        phone: trimmedPhone,
      },
      privacy_audit_log: prependAuditLogEntry(prev?.privacy_audit_log, result?.data?.audit_log_entry),
    }));
    setIsEditing(false);
    showSaveMessage('Profile updated successfully.');
  }, [fullName, phone, navigate, showSaveMessage, dismissSaveMessage]);

  const handleProfileSubmit = useCallback((event) => {
    event.preventDefault();
    if (!saving) handleSave();
  }, [handleSave, saving]);

  const handlePasswordChangeSuccess = useCallback((auditLogEntry) => {
    if (!auditLogEntry) return;
    setSettings((prev) => ({
      ...prev,
      privacy_audit_log: prependAuditLogEntry(prev?.privacy_audit_log, auditLogEntry),
    }));
    showSaveMessage('Password changed successfully.');
  }, [showSaveMessage]);

  const handleDeactivateAccount = useCallback(async () => {
    setDeactivatingAccount(true);
    setDeactivateAccountError('');
    const token = getToken();
    const result = await deactivateUserAccount(navigate);
    if (!result?.ok) {
      setDeactivatingAccount(false);
      setDeactivateAccountError(result?.message ?? 'Failed to deactivate account.');
      return;
    }

    if (token) {
      sessionStorage.setItem(config.STORAGE_KEYS.ACCOUNT_DEACTIVATED_TOKEN, token);
    }
    localStorage.clear();
    setOpenModal(null);
    setDeactivatingAccount(false);
    navigate('/account-deactivated');
  }, [navigate]);

  const closeModal = useCallback(() => setOpenModal(null), []);

  const personalInfo = settings?.personal_info ?? {};
  const accountStatus = settings?.account_status ?? {};
  const coachConnectivity = settings?.coach_connectivity ?? {};
  const auditLogs = settings?.privacy_audit_log ?? [];
  const unusedSessions = coachConnectivity.unused_sessions_count ?? 0;
  const displayName = fullName.trim() || personalInfo.full_name || 'Your profile';

  return (
    <div className={styles.settingsPage} data-page="settings">
      <SuccessToast message={saveMessage} isExiting={saveMessageExiting} />

      <div className={styles.mainContent}>
        <SectionHeader />

        <AnimatePresence mode="wait">
          {loading && (
            <LoadingState key="settings-loading" shouldReduceMotion={shouldReduceMotion} />
          )}

          {error && !loading && (
            <ErrorState key="settings-error" message={error} shouldReduceMotion={shouldReduceMotion} />
          )}

          {!loading && !error && settings && (
            <motion.div
              key="settings-content"
              className={styles.settingsStack}
              variants={pageContainerVariants}
              initial={shouldReduceMotion ? false : 'hidden'}
              animate="show"
            >
            <motion.section
              className={cx(styles.card, styles.identityCard)}
              aria-labelledby="account-status-heading"
              variants={pageSectionVariants}
            >
              <div className={styles.identityHero}>
                <div className={styles.avatar} aria-hidden="true">
                  {initialsFromName(displayName)}
                </div>
                <div className={styles.identityCopy}>
                  <p className={styles.cardEyebrow}>Account</p>
                  <h2 id="account-status-heading" className={styles.identityName}>
                    {displayName}
                  </h2>
                  {personalInfo.email && (
                    <p className={styles.identityEmail}>{personalInfo.email}</p>
                  )}
                  {(accountStatus.is_verified || accountStatus.member_since) && (
                    <div className={styles.identityMeta}>
                      {accountStatus.is_verified && (
                        <span className={styles.verifiedBadge}>
                          <VerifiedIcon />
                          Verified member
                        </span>
                      )}
                      {accountStatus.member_since && (
                        <p className={styles.memberSince}>Since {accountStatus.member_since}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.statusList}>
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>Plan</span>
                  <span className={styles.planBadge}>{accountStatus.plan ?? 'No Plan'}</span>
                </div>
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>Billing</span>
                  <span className={styles.statusValue}>{accountStatus.billing ?? '—'}</span>
                </div>
              </div>
            </motion.section>

            <div className={styles.layoutGrid}>
              <motion.section
                className={cx(styles.card, styles.profileCard)}
                aria-labelledby="personal-info-heading"
                variants={pageSectionVariants}
              >
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeading}>
                    <p className={styles.cardEyebrow}>Profile</p>
                    <h2 id="personal-info-heading" className={styles.cardTitle}>
                      Personal Information
                    </h2>
                    <p className={styles.cardSubtitle}>
                      {isEditing
                        ? 'Update the details associated with your Richuel account.'
                        : 'Your name, email, and phone number.'}
                    </p>
                  </div>
                  <AnimatePresence mode="wait">
                    {!isEditing && (
                      <motion.button
                        key="edit-profile"
                        type="button"
                        className={styles.editButton}
                        onClick={handleEditClick}
                        {...withMotion(editButtonMotion, shouldReduceMotion)}
                      >
                        <PencilIcon />
                        Edit
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>

                {isEditing ? (
                  <motion.form
                    className={styles.formGrid}
                    onSubmit={handleProfileSubmit}
                    variants={nestedStaggerVariants}
                    {...withVariantMotion(shouldReduceMotion)}
                  >
                    <motion.div variants={nestedItemVariants}>
                      <Field
                        id="settings-full-name"
                        label="Full name"
                        type="text"
                        className={styles.fieldGroup}
                        value={fullName}
                        onChange={(event) => setFullName(event.target.value)}
                        autoComplete="name"
                        autoFocus
                      />
                    </motion.div>
                    <motion.div variants={nestedItemVariants}>
                      <Field
                        id="settings-phone"
                        label="Phone number"
                        type="tel"
                        className={styles.fieldGroup}
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        autoComplete="tel"
                      />
                    </motion.div>
                    <motion.div className={styles.fieldSpan} variants={nestedItemVariants}>
                      <Field
                        id="settings-email"
                        label="Email address"
                        type="email"
                        className={styles.fieldGroup}
                        value={personalInfo.email ?? ''}
                        disabled
                        locked
                        hint="Email is tied to your login and cannot be changed here."
                        autoComplete="email"
                      />
                    </motion.div>
                    <AnimatePresence>
                      <motion.div
                        key="profile-form-actions"
                        className={cx(styles.formActions, styles.fieldSpan)}
                        {...withMotion(formActionsMotion, shouldReduceMotion)}
                      >
                        <button type="button" className={styles.cancelButton} onClick={handleCancelEdit} disabled={saving}>
                          Cancel
                        </button>
                        <ActionButton
                          type="submit"
                          className={styles.saveButton}
                          busy={saving}
                          busyLabel="Saving..."
                        >
                          Save Changes
                        </ActionButton>
                      </motion.div>
                    </AnimatePresence>
                  </motion.form>
                ) : (
                  <motion.div className={styles.readGrid} variants={nestedStaggerVariants}>
                    <motion.div variants={nestedItemVariants}>
                      <ReadField label="Full name" value={fullName} />
                    </motion.div>
                    <motion.div variants={nestedItemVariants}>
                      <ReadField label="Phone number" value={phone} />
                    </motion.div>
                    <motion.div className={styles.fieldSpan} variants={nestedItemVariants}>
                      <ReadField
                        label="Email address"
                        value={personalInfo.email}
                        locked
                        hint="Tied to your login"
                      />
                    </motion.div>
                  </motion.div>
                )}
              </motion.section>

              <motion.section
                className={cx(styles.card, styles.securityCard)}
                aria-labelledby="security-privacy-heading"
                variants={pageSectionVariants}
              >
                <div className={styles.cardHeading}>
                  <p className={styles.cardEyebrow}>Protection</p>
                  <h2 id="security-privacy-heading" className={styles.cardTitle}>
                    Security &amp; Privacy
                  </h2>
                  <p className={styles.cardSubtitle}>
                    Keep your account secure and review changes to your data.
                  </p>
                </div>
                <motion.div className={styles.securityGrid} variants={nestedStaggerVariants}>
                  <SecurityItem
                    icon={<PasswordIcon />}
                    title="Change Password"
                    description="Use a unique password."
                    actionLabel="Update"
                    onAction={() => setOpenModal('password')}
                  />
                  <SecurityItem
                    icon={<AuditLogIcon />}
                    title="Privacy Audit Log"
                    description="See when your personal details were last changed."
                    actionLabel="View"
                    onAction={() => setOpenModal('audit')}
                  />
                </motion.div>
              </motion.section>
            </div>

            {coachConnectivity.show && (
              <motion.section
                className={cx(styles.card, styles.coachCard)}
                aria-labelledby="coach-connectivity-heading"
                variants={pageSectionVariants}
              >
                <div className={styles.coachCardInner}>
                  <div className={styles.coachCardContent}>
                    <p className={styles.coachEyebrow}>Coaching</p>
                    <h2 id="coach-connectivity-heading" className={styles.cardTitle}>
                      Coach Connectivity
                    </h2>
                    <p className={styles.coachDescription}>
                      Book unused 1-on-1 time with a CFP this month before it expires.
                    </p>
                  </div>
                  <div className={styles.coachAside}>
                    <div className={styles.coachStat}>
                      <span className={styles.coachStatValue}>{unusedSessions}</span>
                      <span className={styles.coachStatLabel}>
                        unused session{unusedSessions === 1 ? '' : 's'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.scheduleButton}
                      onClick={() => navigate('/dashboard/schedule-cfp-session')}
                    >
                      Schedule Session
                    </button>
                  </div>
                </div>
              </motion.section>
            )}

            <motion.section
              className={styles.dangerZone}
              aria-labelledby="deactivate-account-heading"
              variants={pageSectionVariants}
            >
              <div className={styles.dangerZoneIconWrap} aria-hidden="true">
                <WarningIcon />
              </div>
              <div className={styles.dangerZoneContent}>
                <p className={styles.dangerEyebrow}>Danger zone</p>
                <h2 id="deactivate-account-heading" className={styles.dangerZoneTitle}>
                  Deactivate Account
                </h2>
                <p className={styles.dangerZoneDescription}>
                  Signing out starts a 30-day removal of your Richuel data and coaching history.
                  This cannot be undone.
                </p>
              </div>
              <button
                type="button"
                className={styles.deactivateAccountButton}
                onClick={() => {
                  setDeactivateAccountError('');
                  setOpenModal('deactivate');
                }}
              >
                Deactivate
              </button>
            </motion.section>

            <motion.footer className={styles.settingsFooter} variants={pageSectionVariants}>
              <p className={styles.settingsFooterText}>
                <span className={styles.settingsFooterLock} aria-hidden="true">
                  <LockIcon />
                </span>
                <span>
                  © {FOOTER_YEAR} Richuel Financial Wellness. Secure 256-bit AES encryption enabled.
                </span>
              </p>
            </motion.footer>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ChangePasswordModal
        isOpen={openModal === 'password'}
        onClose={closeModal}
        onSuccess={handlePasswordChangeSuccess}
        shouldReduceMotion={shouldReduceMotion}
      />
      <AuditLogModal
        isOpen={openModal === 'audit'}
        onClose={closeModal}
        logs={auditLogs}
        shouldReduceMotion={shouldReduceMotion}
      />
      <DeactivateAccountModal
        isOpen={openModal === 'deactivate'}
        onClose={closeModal}
        onConfirm={handleDeactivateAccount}
        deactivating={deactivatingAccount}
        error={deactivateAccountError}
        shouldReduceMotion={shouldReduceMotion}
      />
    </div>
  );
};

export default SettingsPage;
